import { useSubmitFinalizedReport } from '@/hooks/api/finalizedReports'
import { useSession } from '@/shared/hooks/useSession'
import { Button, CheckboxGroup } from '@/common'

import { GenericDialog } from '@/common'
import { CheckCircleIcon } from '@/common/components/icons'
import { useState, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

export default function SubmitFinalizedReportDialog({
  isOpen,
  close,
  installationId,
  finalizedReportId,
  selectedPdfLanguage
}: {
  isOpen: boolean
  close: () => void
  installationId: string
  finalizedReportId: string
  selectedPdfLanguage?: string
}) {
  const {
    session: {
      organization: { id: organizationId }
    }
  } = useSession()

  const { t } = useTranslation()

  const { mutateAsync: submitFinalizedReport, isPending } = useSubmitFinalizedReport(
    organizationId,
    installationId,
    finalizedReportId
  )

  const [confirmedReview, setConfirmedReview] = useState(false)
  const [confirmedCbamPurposes, setConfirmedCbamPurposes] = useState(false)
  const [confirmedErrorsAndOmissions, setConfirmedErrorsAndOmissions] = useState(false)

  const onClose = useCallback(() => {
    setConfirmedReview(false)
    setConfirmedCbamPurposes(false)
    setConfirmedErrorsAndOmissions(false)
    close()
  }, [close])
  const onFinalize = async () => {
    const finalizedReport = await submitFinalizedReport({ lang: selectedPdfLanguage })
    if (finalizedReport) {
      toast.success('Report submitted successfully')
      onClose()
    } else {
      toast.error('Failed to submit report')
    }
  }

  const isFinalizeEnabled = confirmedReview && confirmedCbamPurposes && confirmedErrorsAndOmissions

  return (
    <GenericDialog
      title={t('report:finalisedReport.title')}
      subtitle={t('report:finalisedReport.subtitle')}
      icon={<CheckCircleIcon className="size-6 text-green-600" />}
      size="2xl"
      isOpen={isOpen}
      close={close}
    >
      <div className="space-y-4 mb-6">
        <CheckboxGroup
          id="confirm-report-review-accuracy"
          checked={confirmedReview}
          onChange={() => setConfirmedReview(!confirmedReview)}
          label={t('report:finalisedReport.confirmReportReviewAccuracy')}
        />
        <CheckboxGroup
          id="confirm-cbam-reporting-purpose"
          checked={confirmedCbamPurposes}
          onChange={() => setConfirmedCbamPurposes(!confirmedCbamPurposes)}
          label={t('report:finalisedReport.confirmCBAMReportingPurpose')}
        />
        <CheckboxGroup
          id="confirm-error-communication"
          checked={confirmedErrorsAndOmissions}
          onChange={() => setConfirmedErrorsAndOmissions(!confirmedErrorsAndOmissions)}
          label={t('report:finalisedReport.confirmErrorCommunication')}
        />
      </div>
      <footer className="flex justify-end gap-2">
        <Button variant="neutral" onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          disabled={!isFinalizeEnabled || isPending}
          isLoading={isPending}
          onClick={onFinalize}
        >
          {t('common:actions.submit')}
        </Button>
      </footer>
    </GenericDialog>
  )
}
